// Imported from the `@vellum/ui/environment` subpath rather than the root
// barrel: that module is a dependency-free leaf, so pulling it in here does not
// drag the component barrel (or a module-graph cycle) into this package. Same
// arrangement as `@vellum/ui/fetch` in ./vellumApiClient.
import type { HostedEnvironment } from "@vellum/ui/environment";

/**
 * The environments this resolver can distinguish. An alias of the shared
 * `HostedEnvironment` in `@vellum/ui/environment` — the browser can only tell
 * these three apart by hostname, and they are the only ones with real hosts.
 */
export type Environment = HostedEnvironment;

/**
 * Resolve the deployment environment from the page host: local dev boxes use
 * `*.local.vibestack.cloud` (or plain `localhost`), the shared dev stack uses
 * `*.dev.vellum.health`, and everything else is treated as prod.
 *
 * Distinct from `normalizeEnvironment` in `@vellum/ui/environment`, which turns
 * a raw server-side `ENVIRONMENT` value into an `Environment`. This one asks
 * "where is this page running?"; that one asks "what does this string mean?".
 */
export function resolveEnvironment(): Environment {
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host.endsWith(".local.vibestack.cloud")) {
      return "local";
    }
    if (host.endsWith(".dev.vellum.health")) {
      return "dev";
    }
  }
  // No `window` — SSR, or Next's Edge bundle pulling this in through the
  // `@vellum/ui` barrel. `import.meta.env` is Vite-only and is `undefined`
  // under webpack/Turbopack, so key off NODE_ENV, which both bundlers inline.
  // Anything we can't identify is treated as prod.
  const isDev =
    typeof process !== "undefined" && process.env?.NODE_ENV !== "production";
  return isDev ? "local" : "prod";
}
